<template>
  <div class="min-h-screen bg-gray-100 p-8">
    <div class="max-w-4xl mx-auto">
      <h1 class="text-3xl text-gray-800 mb-6 text-center">
        Test - Suppression d'arrière-plan avec fond vert
      </h1>

      <div class="text-center mb-6">
        <button
          v-if="!cameraActive"
          @click="startCamera"
          class="bg-blue-600 text-white px-8 py-3 rounded hover:bg-blue-700 text-lg font-semibold"
        >
          Activer la caméra
        </button>
        <button
          v-else
          @click="stopCamera"
          class="bg-red-600 text-white px-8 py-3 rounded hover:bg-red-700 text-lg font-semibold"
        >
          Arrêter la caméra
        </button>
      </div>

      <div class="relative bg-white rounded-lg shadow-lg p-4">
        <!-- Vidéo source (cachée) -->
        <video
          ref="videoElement"
          autoplay
          playsinline
          class="hidden"
        ></video>

        <!-- Canvas pour afficher le résultat avec fond vert -->
        <canvas
          ref="canvasOutput"
          width="640"
          height="480"
          class="w-full rounded-lg bg-gray-200"
          style="max-width: 100%; height: auto;"
        ></canvas>

        <div v-if="loading" class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
          <div class="text-white text-lg">Chargement du modèle BodyPix...</div>
        </div>

        <div v-if="error" class="mt-4 p-4 bg-red-100 text-red-700 rounded">
          {{ error }}
        </div>

        <div v-if="!cameraActive && !loading && !error" class="absolute inset-0 flex items-center justify-center">
          <div class="text-gray-500 text-lg">Cliquez sur "Activer la caméra" pour commencer</div>
        </div>
      </div>

      <div class="mt-6 text-center text-gray-600">
        <p class="text-sm">
          Technologie : <span class="font-semibold">{{ processorType }}</span>
        </p>
        <p class="text-xs mt-2 text-gray-500">
          Fond personnalisé : <span class="font-semibold">{{ backgroundImage ? '/photo.jpg chargé ✓' : 'Fond vert par défaut' }}</span>
        </p>
        <div class="mt-4 space-x-2">
          <button
            @click="switchProcessor('mediapipe')"
            :class="processorType === 'MediaPipe Selfie Segmentation' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'"
            class="px-4 py-2 rounded text-sm"
          >
            MediaPipe
          </button>
          <button
            @click="switchProcessor('videosdk')"
            :class="processorType === 'VideoSDK Media Processor' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'"
            class="px-4 py-2 rounded text-sm"
          >
            VideoSDK
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      cameraActive: false,
      loading: false,
      error: null,
      stream: null,
      selfieSegmentation: null,
      videoSDKProcessor: null,
      animationId: null,
      backgroundImage: null,
      currentProcessor: 'mediapipe', // 'mediapipe' ou 'videosdk'
      processorType: 'MediaPipe Selfie Segmentation',
    };
  },
  async mounted() {
    // Charger l'image de fond
    const img = new Image();
    img.onload = () => {
      this.backgroundImage = img;
    };
    img.onerror = () => {
      console.error('Impossible de charger photo.jpg');
    };
    img.src = '/photo.jpg';
  },
  beforeDestroy() {
    this.stopCamera();
  },
  methods: {
    async startCamera() {
      try {
        this.error = null;
        this.loading = true;

        if (this.currentProcessor === 'mediapipe') {
          await this.startMediaPipe();
        } else {
          await this.startVideoSDK();
        }

        this.loading = false;
        this.cameraActive = true;
        this.processFrame();
      } catch (err) {
        console.error('Erreur:', err);
        this.error = `Erreur: ${err.message}`;
        this.loading = false;
        this.cameraActive = false;
      }
    },

    async startMediaPipe() {
      // Charger MediaPipe si nécessaire
      if (!window.SelfieSegmentation) {
        await this.loadMediaPipe();
      }

      // Démarrer la caméra
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });

      const video = this.$refs.videoElement;
      video.srcObject = this.stream;
      await new Promise((resolve) => { video.onloadedmetadata = resolve; });
      await video.play();

      // Initialiser MediaPipe
      if (!this.selfieSegmentation) {
        this.selfieSegmentation = new window.SelfieSegmentation({
          locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/${file}`
        });

        this.selfieSegmentation.setOptions({
          modelSelection: 1,
          selfieMode: false,
        });

        this.selfieSegmentation.onResults((results) => this.onResults(results));
      }
    },

    async startVideoSDK() {
      // Charger VideoSDK/BodyPix si nécessaire
      if (!window.bodyPix) {
        await this.loadVideoSDK();
      }

      // Démarrer la caméra
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });

      const video = this.$refs.videoElement;
      video.srcObject = this.stream;
      await new Promise((resolve) => { video.onloadedmetadata = resolve; });
      await video.play();

      // Initialiser BodyPix avec paramètres optimisés
      if (!this.videoSDKProcessor) {
        this.videoSDKProcessor = await window.bodyPix.load({
          architecture: 'MobileNetV1',
          outputStride: 16,
          multiplier: 0.75,
          quantBytes: 2,
        });
      }
    },

    async processFrame() {
      if (!this.cameraActive) return;

      const video = this.$refs.videoElement;

      try {
        if (this.currentProcessor === 'mediapipe' && this.selfieSegmentation) {
          // MediaPipe
          await this.selfieSegmentation.send({ image: video });
        } else if (this.currentProcessor === 'videosdk' && this.videoSDKProcessor) {
          // VideoSDK/BodyPix
          await this.processVideoSDK(video);
        }
      } catch (err) {
        console.error('Erreur de segmentation:', err);
      }

      // Continuer le traitement en boucle
      this.animationId = requestAnimationFrame(() => this.processFrame());
    },

    async processVideoSDK(video) {
      const canvas = this.$refs.canvasOutput;
      const ctx = canvas.getContext('2d');

      if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
      }

      const width = canvas.width;
      const height = canvas.height;

      // Segmentation avec BodyPix
      const segmentation = await this.videoSDKProcessor.segmentPerson(video, {
        flipHorizontal: false,
        internalResolution: 'medium',
        segmentationThreshold: 0.7,
        maxDetections: 1,
      });

      // Dessiner le fond
      if (this.backgroundImage) {
        ctx.drawImage(this.backgroundImage, 0, 0, width, height);
      } else {
        ctx.fillStyle = '#00FF00';
        ctx.fillRect(0, 0, width, height);
      }

      // Dessiner la vidéo
      ctx.drawImage(video, 0, 0, width, height);

      // Appliquer le masque
      const imageData = ctx.getImageData(0, 0, width, height);
      const pixels = imageData.data;
      const mask = segmentation.data;

      for (let i = 0; i < mask.length; i++) {
        if (mask[i] === 0) { // Arrière-plan
          const pixelIndex = i * 4;
          if (this.backgroundImage) {
            // Garder le fond de l'image
          } else {
            pixels[pixelIndex] = 0;       // R
            pixels[pixelIndex + 1] = 255; // G
            pixels[pixelIndex + 2] = 0;   // B
          }
        }
      }

      ctx.putImageData(imageData, 0, 0);
    },

    onResults(results) {
      // Cette méthode est appelée par MediaPipe avec les résultats
      const canvas = this.$refs.canvasOutput;
      const ctx = canvas.getContext('2d');

      // Définir les dimensions du canvas
      canvas.width = results.image.width;
      canvas.height = results.image.height;

      const width = canvas.width;
      const height = canvas.height;

      // Activer l'anti-aliasing pour un rendu plus lisse
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Créer un canvas temporaire pour le masque avec échelle supérieure
      const scale = 2; // Double résolution pour meilleur anti-aliasing
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = width * scale;
      tempCanvas.height = height * scale;
      const tempCtx = tempCanvas.getContext('2d');

      tempCtx.imageSmoothingEnabled = true;
      tempCtx.imageSmoothingQuality = 'high';

      // Dessiner le masque à une résolution supérieure
      tempCtx.drawImage(results.segmentationMask, 0, 0, width * scale, height * scale);

      // Appliquer un flou minimal pour adoucir les transitions
      tempCtx.filter = 'blur(1px)';
      tempCtx.drawImage(tempCanvas, 0, 0);
      tempCtx.filter = 'none';

      // Étape 1: Dessiner le fond (image ou vert par défaut)
      if (this.backgroundImage) {
        ctx.drawImage(this.backgroundImage, 0, 0, width, height);
      } else {
        ctx.fillStyle = '#00FF00';
        ctx.fillRect(0, 0, width, height);
      }

      // Étape 2: Sauvegarder le contexte
      ctx.save();

      // Étape 3: Dessiner la vidéo avec anti-aliasing
      ctx.drawImage(results.image, 0, 0, width, height);

      // Étape 4: Utiliser le masque haute résolution pour découper (downscale automatique = anti-aliasing)
      ctx.globalCompositeOperation = 'destination-in';
      ctx.drawImage(tempCanvas, 0, 0, width, height);

      // Étape 5: Restaurer et dessiner le fond derrière
      ctx.globalCompositeOperation = 'destination-over';
      if (this.backgroundImage) {
        ctx.drawImage(this.backgroundImage, 0, 0, width, height);
      } else {
        ctx.fillStyle = '#00FF00';
        ctx.fillRect(0, 0, width, height);
      }

      // Restaurer le contexte
      ctx.restore();
    },

    stopCamera() {
      this.cameraActive = false;

      // Arrêter l'animation
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }

      // Arrêter le flux vidéo
      if (this.stream) {
        this.stream.getTracks().forEach((track) => track.stop());
        this.stream = null;
      }

      // Nettoyer le canvas
      const canvas = this.$refs.canvasOutput;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    },

    async loadMediaPipe() {
      return new Promise((resolve, reject) => {
        // Charger MediaPipe Selfie Segmentation
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation/selfie_segmentation.js';
        script.onload = () => {
          // Charger Camera Utils
          const cameraScript = document.createElement('script');
          cameraScript.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/camera_utils/camera_utils.js';
          cameraScript.onload = resolve;
          cameraScript.onerror = reject;
          document.head.appendChild(cameraScript);
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });
    },

    async loadVideoSDK() {
      return new Promise((resolve, reject) => {
        // Charger TensorFlow.js pour VideoSDK
        if (!window.tf) {
          const tfScript = document.createElement('script');
          tfScript.src = 'https://cdn.jsdelivr.net/npm/@tensorflow/tfjs@4.11.0/dist/tf.min.js';
          tfScript.onload = () => {
            // Charger BodyPix (utilisé par VideoSDK)
            const bodyPixScript = document.createElement('script');
            bodyPixScript.src = 'https://cdn.jsdelivr.net/npm/@tensorflow-models/body-pix@2.2.0/dist/body-pix.min.js';
            bodyPixScript.onload = resolve;
            bodyPixScript.onerror = reject;
            document.head.appendChild(bodyPixScript);
          };
          tfScript.onerror = reject;
          document.head.appendChild(tfScript);
        } else {
          resolve();
        }
      });
    },

    async switchProcessor(type) {
      // Arrêter la caméra si active
      if (this.cameraActive) {
        this.stopCamera();
      }

      this.currentProcessor = type;
      this.processorType = type === 'mediapipe'
        ? 'MediaPipe Selfie Segmentation'
        : 'VideoSDK Media Processor';

      // Redémarrer si nécessaire
      if (this.stream) {
        await this.startCamera();
      }
    },
  },
};
</script>
